/**
 * Dev Console → Web Sources (ADR-038).
 *
 * The sites Fias approved for apps to read, and the platform switch. An app
 * may read any page on an active site and gets back only values in the form
 * it declares, never the page's text. Approving a site is the security
 * decision, so the page shows the approval checklist and asks the admin to
 * confirm it for every site they add. Server side is `routes/admin/web-sources.ts`:
 * admin-only, every site change audited (`web_source.site_*`), the switch
 * audited in `platform_settings_audit`.
 */

import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Globe } from 'lucide-react';
import {
  ApiError,
  api,
  type WebSourceSiteCategory,
  type WebSourceReaderModelCandidateDto,
  type WebSourceSiteDto,
  type WebSourcesReaderDto,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';

const CATEGORY_LABELS: Record<WebSourceSiteCategory, string> = {
  credit_union: 'Credit union',
  bank: 'Bank',
  government: 'Government',
  other: 'Other',
};

const CATEGORIES = Object.keys(CATEGORY_LABELS) as WebSourceSiteCategory[];

/** ADR-038 §1: a site is approved only when all of these hold. */
const APPROVAL_CHECKLIST = [
  'An accountable organisation runs it, and it is not the developer of an app that will read it (federally insured credit unions and banks, and government bodies, are the model case).',
  'It is served over HTTPS.',
  'Nothing on the approved part is written by the public — comments, reviews, forums, search results that echo what was typed. Exclude any such paths below, or do not approve the site.',
  'Its robots.txt and terms allow automated reading.',
];

const PAGE_SIZE = 100;

const SELECT_CLASS = 'h-9 rounded-md border border-input bg-background px-2 text-sm';

function errorMessage(err: unknown): string {
  return err instanceof ApiError || err instanceof Error ? err.message : 'Something went wrong';
}

/**
 * What reader settings make a page's reading cost, as a multiple of the
 * default, to one decimal place: the server's `readerPriceMultiple`, which
 * decides; this copy only shows the number the admin will be asked to type.
 */
function readerPriceMultiple(minReads: number, priceRatio: number): number {
  return Math.round(minReads * priceRatio * 10) / 10;
}

function parsePaths(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
}

function SwitchCard(): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const setting = useQuery({
    queryKey: queryKeys.adminWebSources.switch(),
    queryFn: () => api.adminGetWebSourcesSwitch(),
  });
  const setEnabled = useMutation({
    mutationFn: (enabled: boolean) => api.adminSetWebSourcesSwitch(enabled),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.adminWebSources.switch(), data);
      toast({ title: data.enabled ? 'Apps can read web sources' : 'Web sources turned off' });
    },
    onError: (err) =>
      toast({
        title: 'Could not change the switch',
        description: errorMessage(err),
        variant: 'destructive',
      }),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Let apps read web sources</CardTitle>
        <CardDescription>
          Off until someone turns it on, and audited. While it is off every app call is refused; the
          site list below still works, so sites can be prepared first. Other servers follow within
          half a minute.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor="web-sources-enabled">Apps may read pages on approved sites</Label>
          <Switch
            id="web-sources-enabled"
            aria-label="Apps may read pages on approved sites"
            checked={setting.data?.enabled === true}
            disabled={setting.isLoading || setEnabled.isPending}
            onCheckedChange={(checked) => setEnabled.mutate(checked)}
          />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * The reader (ADR-038 §4): how many independent readings every page gets at
 * least, and which model reads. Two readings cost two reader calls, paid by
 * the app's user; a value the readings disagree on comes back empty for a
 * person to confirm. The reader asks every model for temperature 0; only a
 * model that takes a temperature honors it. What the settings cost is held
 * to a ceiling by the server, and a raise must be typed.
 */
function ReaderCard(): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const reader = useQuery({
    queryKey: queryKeys.adminWebSources.reader(),
    queryFn: () => api.adminGetWebSourcesReader(),
  });
  // The form holds the admin's edits; it is seeded from the server once, and
  // again only after a save, so a background refetch never wipes an edit.
  const [form, setForm] = useState<{ minReads: 1 | 2; readerModelId: string } | null>(null);
  // A raise in price is typed, like a raised AdOpt limit.
  const [typedPrice, setTypedPrice] = useState('');
  useEffect(() => {
    if (form === null && reader.data) {
      // A malformed row starts the form at the defaults: saving replaces it.
      setForm({
        minReads: reader.data.settings?.minReads ?? 1,
        readerModelId: reader.data.settings?.readerModelId ?? '',
      });
    }
  }, [form, reader.data]);
  const minReads = form?.minReads ?? 1;
  const readerModelId = form?.readerModelId ?? '';
  const candidates = reader.data?.candidates ?? [];
  const pointer = candidates.find((c) => c.id === reader.data?.capabilityModelId);
  const chosen = candidates.find((c) => c.id === readerModelId);
  const effective = readerModelId === '' ? pointer : chosen;
  const honorsTemperature = effective?.supportsSamplingParams;
  // Options and price checks render only once the settings have loaded.
  const maxPriceMultiple = reader.data?.maxPriceMultiple ?? null;
  // The default model costs one reading's worth by definition.
  const priceRatio = readerModelId === '' ? 1 : (chosen?.priceRatio ?? null);
  const priceMultiple = priceRatio === null ? null : readerPriceMultiple(minReads, priceRatio);
  // Stored settings the server cannot price (or a malformed row) count as the default, as there.
  const storedPriceMultiple = reader.data?.priceMultiple ?? 1;
  const aboveCeiling =
    maxPriceMultiple !== null && priceMultiple !== null && priceMultiple > maxPriceMultiple;
  const raisesPrice =
    !!reader.data && priceMultiple !== null && priceMultiple > storedPriceMultiple;
  const priceConfirmed =
    !raisesPrice || (typedPrice.trim() !== '' && Number(typedPrice) === priceMultiple);
  const save = useMutation({
    mutationFn: () =>
      api.adminSetWebSourcesReader({
        minReads,
        readerModelId: readerModelId === '' ? null : readerModelId,
        ...(raisesPrice ? { confirmPriceMultiple: Number(typedPrice) } : {}),
      }),
    onSuccess: (data: WebSourcesReaderDto) => {
      queryClient.setQueryData(queryKeys.adminWebSources.reader(), data);
      setForm({
        minReads: data.settings?.minReads ?? 1,
        readerModelId: data.settings?.readerModelId ?? '',
      });
      setTypedPrice('');
      toast({ title: 'Reader settings saved' });
    },
    onError: (err) =>
      toast({
        title: 'Could not save the reader settings',
        description: errorMessage(err),
        variant: 'destructive',
      }),
  });
  /** Why a model cannot be chosen, or its price next to the default's, for the option label. */
  const optionNote = (c: WebSourceReaderModelCandidateDto): string => {
    if (!c.published) return ' (not published)';
    if (!c.supportsSamplingParams) return ' (takes no temperature)';
    if (!c.usable || c.priceRatio === null) return ' (the reader cannot use it)';
    if (readerPriceMultiple(minReads, c.priceRatio) > (maxPriceMultiple ?? 0)) {
      return ' (above the price ceiling)';
    }
    const multiple = readerPriceMultiple(1, c.priceRatio);
    return multiple > 1 ? `, about ${multiple}x the default’s price` : '';
  };
  const choosable = (c: WebSourceReaderModelCandidateDto): boolean =>
    c.published &&
    c.usable &&
    c.priceRatio !== null &&
    readerPriceMultiple(minReads, c.priceRatio) <= (maxPriceMultiple ?? 0);
  const dirty =
    !!reader.data &&
    (reader.data.malformed ||
      minReads !== reader.data.settings?.minReads ||
      (readerModelId === '' ? null : readerModelId) !== reader.data.settings?.readerModelId);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">The reader</CardTitle>
        <CardDescription>
          A model reading a page is not deterministic: the same page can yield a different number on
          a second reading. With two readings only a value both agree on is returned, and the rest
          come back empty for a person to confirm. Apps ask per call; this floor applies to every
          app. Each reading is a reader call paid by the app&apos;s user, at the chosen model&apos;s
          price — so raising the floor or picking a dearer model raises what every app&apos;s users
          pay, which their developers cannot see. A raise has to be typed
          {maxPriceMultiple !== null
            ? `, and nothing above ${maxPriceMultiple}x the default can be set`
            : ''}
          .
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {reader.isError && (
          <p role="alert" className="text-sm text-destructive">
            Could not load the reader settings: {errorMessage(reader.error)}
          </p>
        )}
        {reader.data?.malformed && (
          <p role="alert" className="text-sm text-destructive">
            The stored reader settings are malformed, so every page read is refused until they are
            saved over. Choose the settings below and save.
          </p>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="web-sources-min-reads">Readings per page, at least</Label>
            <select
              id="web-sources-min-reads"
              className={SELECT_CLASS}
              value={minReads}
              disabled={form === null || save.isPending}
              onChange={(e) =>
                setForm({ minReads: Number(e.target.value) as 1 | 2, readerModelId })
              }
            >
              <option value={1}>1 — one reading (apps may ask for two)</option>
              <option value={2}>2 — two readings that must agree, for every app</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="web-sources-reader-model">Reader model</Label>
            <select
              id="web-sources-reader-model"
              className={SELECT_CLASS}
              value={readerModelId}
              disabled={form === null || save.isPending}
              onChange={(e) => setForm({ minReads, readerModelId: e.target.value })}
            >
              <option value="">
                Platform default (text-fast{pointer ? `: ${pointer.displayName}` : ''})
              </option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id} disabled={!choosable(c)}>
                  {c.displayName} — {c.modelName}
                  {optionNote(c)}
                </option>
              ))}
            </select>
          </div>
        </div>
        {reader.data && effective && (
          <p className="text-sm text-muted-foreground">
            {honorsTemperature
              ? 'This model honors the reader’s temperature 0: the same page and form read the same way each time. Two readings then catch little; they are most useful on a model that samples.'
              : 'This model takes no temperature, so it samples as it will; two readings remove the values it is unsure of (they do not prove a value right).'}
          </p>
        )}
        {reader.data && priceMultiple !== null && (
          <p className="text-sm text-muted-foreground">
            With these settings a page reading costs about {priceMultiple}x the default
            {dirty && reader.data.priceMultiple !== null
              ? ` (${reader.data.priceMultiple}x now)`
              : ''}
            .
          </p>
        )}
        {aboveCeiling && (
          <p role="alert" className="text-sm text-destructive">
            That is above the most the reader may cost, {maxPriceMultiple}x the default.
          </p>
        )}
        {raisesPrice && !aboveCeiling && (
          <div className="space-y-1">
            <Label htmlFor="web-sources-reader-price">
              This raises what every app’s users pay per reading. Type {priceMultiple} to confirm.
            </Label>
            <Input
              id="web-sources-reader-price"
              inputMode="decimal"
              autoComplete="off"
              className="max-w-[8rem]"
              value={typedPrice}
              disabled={save.isPending}
              onChange={(e) => setTypedPrice(e.target.value)}
            />
          </div>
        )}
        <Button
          size="sm"
          disabled={
            !dirty || save.isPending || priceMultiple === null || aboveCeiling || !priceConfirmed
          }
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save reader settings'}
        </Button>
      </CardContent>
    </Card>
  );
}

function AddSiteCard(): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [host, setHost] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState<WebSourceSiteCategory>('credit_union');
  const [note, setNote] = useState('');
  const [paths, setPaths] = useState('');
  const [checked, setChecked] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () =>
      api.adminAddWebSourceSite({
        host: host.trim(),
        name: name.trim(),
        category,
        ...(note.trim() ? { note: note.trim() } : {}),
        excludedPathPrefixes: parsePaths(paths),
      }),
    onSuccess: ({ site }) => {
      toast({ title: `${site.host} approved` });
      setHost('');
      setName('');
      setNote('');
      setPaths('');
      setChecked(false);
      setProblem(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminWebSources.all });
    },
    onError: (err) => setProblem(errorMessage(err)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Approve a site</CardTitle>
        <CardDescription>
          Apps will be able to read any page on this host and its <code>www.</code> twin. Approve a
          site only when all of these hold:
        </CardDescription>
        <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
          {APPROVAL_CHECKLIST.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ol>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="site-host">Host</Label>
            <Input
              id="site-host"
              placeholder="www.becu.org"
              value={host}
              onChange={(e) => setHost(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="site-name">Name</Label>
            <Input
              id="site-name"
              placeholder="BECU"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="site-category">Category</Label>
            <select
              id="site-category"
              className={`${SELECT_CLASS} w-full`}
              value={category}
              onChange={(e) => setCategory(e.target.value as WebSourceSiteCategory)}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="site-note">Why it passed review</Label>
            <Input
              id="site-note"
              placeholder="NCUA charter 5536 lists this website"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="site-paths">Excluded paths (one per line, optional)</Label>
          <Textarea
            id="site-paths"
            rows={2}
            placeholder="/community/"
            value={paths}
            onChange={(e) => setPaths(e.target.value)}
          />
        </div>
        <div className="flex items-start gap-2">
          <Checkbox
            id="site-checklist"
            checked={checked}
            onCheckedChange={setChecked}
            aria-label="This site meets all four conditions"
          />
          <Label htmlFor="site-checklist" className="text-sm font-normal">
            I checked this site against all four conditions.
          </Label>
        </div>
        {problem && (
          <p className="text-sm text-destructive" role="alert">
            {problem}
          </p>
        )}
        <Button
          disabled={add.isPending || !checked || host.trim() === '' || name.trim() === ''}
          onClick={() => add.mutate()}
        >
          Approve site
        </Button>
      </CardContent>
    </Card>
  );
}

type PendingAction =
  | { kind: 'suspend'; site: WebSourceSiteDto }
  | { kind: 'remove'; site: WebSourceSiteDto }
  | { kind: 'paths'; site: WebSourceSiteDto };

function SitesCard(): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [reason, setReason] = useState('');
  const [paths, setPaths] = useState('');

  const params = { search: search.trim(), category, status, offset };
  const sites = useQuery({
    queryKey: queryKeys.adminWebSources.sites(params),
    queryFn: () =>
      api.adminListWebSourceSites({
        ...(params.search ? { search: params.search } : {}),
        ...(category ? { category: category as WebSourceSiteCategory } : {}),
        ...(status ? { status: status as 'active' | 'suspended' } : {}),
        limit: PAGE_SIZE,
        offset,
      }),
  });
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.adminWebSources.all });
  const update = useMutation({
    mutationFn: (input: {
      host: string;
      body: Parameters<typeof api.adminUpdateWebSourceSite>[1];
    }) => api.adminUpdateWebSourceSite(input.host, input.body),
    onSuccess: invalidate,
    onError: (err) =>
      toast({
        title: 'Could not change the site',
        description: errorMessage(err),
        variant: 'destructive',
      }),
  });
  const remove = useMutation({
    mutationFn: (host: string) => api.adminRemoveWebSourceSite(host),
    onSuccess: invalidate,
    onError: (err) =>
      toast({
        title: 'Could not remove the site',
        description: errorMessage(err),
        variant: 'destructive',
      }),
  });

  const total = sites.data?.total ?? 0;
  const close = () => {
    setPending(null);
    setReason('');
    setPaths('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Approved sites {sites.data ? `(${total})` : ''}</CardTitle>
        <CardDescription>
          A site whose address fails 20 times in a row (it stops resolving or connecting) is
          suspended automatically; restore it once you have checked it is still the same
          organisation&apos;s site.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Input
            aria-label="Search sites"
            placeholder="Search name or host…"
            className="max-w-xs"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOffset(0);
            }}
          />
          <select
            aria-label="Category"
            className={SELECT_CLASS}
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
          <select
            aria-label="Status"
            className={SELECT_CLASS}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">Any status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>

        {sites.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {sites.isError && (
          <p className="text-sm text-destructive" role="alert">
            Could not load the sites.
          </p>
        )}
        {sites.data && sites.data.sites.length === 0 && (
          <p className="text-sm text-muted-foreground">No sites match.</p>
        )}
        {sites.data && sites.data.sites.length > 0 && (
          <ul className="flex flex-col divide-y divide-border border-y border-border">
            {sites.data.sites.map((site) => (
              <li key={site.host} className="flex flex-wrap items-start justify-between gap-3 py-2">
                <div className="min-w-0 space-y-0.5">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                    {site.name}
                    <Badge variant="outline">{CATEGORY_LABELS[site.category]}</Badge>
                    {site.status === 'suspended' && <Badge variant="destructive">Suspended</Badge>}
                  </p>
                  <p className="truncate font-mono text-xs text-muted-foreground">{site.host}</p>
                  {site.note && <p className="text-xs text-muted-foreground">{site.note}</p>}
                  {site.excludedPathPrefixes.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Excluded: {site.excludedPathPrefixes.join(', ')}
                    </p>
                  )}
                  {site.status === 'suspended' && site.suspendedReason && (
                    <p className="text-xs text-destructive">{site.suspendedReason}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setPaths(site.excludedPathPrefixes.join('\n'));
                      setPending({ kind: 'paths', site });
                    }}
                  >
                    Excluded paths
                  </Button>
                  {site.status === 'active' ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setPending({ kind: 'suspend', site })}
                    >
                      Suspend
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={update.isPending}
                      onClick={() =>
                        update.mutate(
                          { host: site.host, body: { status: 'active' } },
                          { onSuccess: () => toast({ title: `${site.host} restored` }) },
                        )
                      }
                    >
                      Restore
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setPending({ kind: 'remove', site })}
                  >
                    Remove
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-end gap-2 text-sm">
            <Button
              size="sm"
              variant="outline"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <span className="text-muted-foreground">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        )}
      </CardContent>

      <AlertDialog open={pending !== null} onOpenChange={(open) => !open && close()}>
        <AlertDialogContent>
          {pending?.kind === 'remove' && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove {pending.site.host}?</AlertDialogTitle>
                <AlertDialogDescription>
                  Apps stop reading it at once, and its cached pages and readings are deleted. The
                  change is audited.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    const host = pending.site.host;
                    remove.mutate(host, { onSuccess: () => toast({ title: `${host} removed` }) });
                    close();
                  }}
                >
                  Remove
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
          {pending?.kind === 'suspend' && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Suspend {pending.site.host}?</AlertDialogTitle>
                <AlertDialogDescription>
                  Apps stop reading it at once; its cache is kept until you restore or remove it.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-1">
                <Label htmlFor="suspend-reason">Reason</Label>
                <Input
                  id="suspend-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={reason.trim() === ''}
                  onClick={() => {
                    const host = pending.site.host;
                    update.mutate(
                      { host, body: { status: 'suspended', suspendedReason: reason.trim() } },
                      { onSuccess: () => toast({ title: `${host} suspended` }) },
                    );
                    close();
                  }}
                >
                  Suspend
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
          {pending?.kind === 'paths' && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Excluded paths on {pending.site.host}</AlertDialogTitle>
                <AlertDialogDescription>
                  Parts of the site where the public can post. One path prefix per line, each
                  starting with &quot;/&quot;.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <Textarea
                aria-label="Excluded paths"
                rows={4}
                value={paths}
                onChange={(e) => setPaths(e.target.value)}
              />
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    const host = pending.site.host;
                    update.mutate(
                      { host, body: { excludedPathPrefixes: parsePaths(paths) } },
                      { onSuccess: () => toast({ title: `Excluded paths saved for ${host}` }) },
                    );
                    close();
                  }}
                >
                  Save
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export function WebSourcesPage(): ReactElement {
  return (
    <div className="container mx-auto max-w-4xl space-y-6 py-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/dev-console">
            <ArrowLeft className="mr-1 h-4 w-4" aria-hidden />
            Dev Console
          </Link>
        </Button>
      </div>
      <div className="space-y-1">
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <Globe className="h-5 w-5" aria-hidden />
          Web sources
        </h1>
        <p className="text-sm text-muted-foreground">
          Apps read pages on these sites and receive only values in the form they ask for — never a
          page&apos;s text (ADR-038).
        </p>
      </div>
      <SwitchCard />
      <ReaderCard />
      <AddSiteCard />
      <SitesCard />
    </div>
  );
}
